import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Search, RefreshCw } from 'lucide-react'
import { getFolderPriorArt, reEvaluateFolderPriorArt, getReprocessStatus } from '../../services/api'
import type { ReprocessJob } from '../../types'
import ScoreDisplay from '../shared/ScoreDisplay'

interface FolderPriorArtPanelProps {
  projectId: string
  folderId: string
}

export default function FolderPriorArtPanel({ projectId, folderId }: FolderPriorArtPanelProps) {
  const queryClient = useQueryClient()
  const [reprocessJob, setReprocessJob] = useState<ReprocessJob | null>(null)

  const { data: priorArt, isLoading } = useQuery({
    queryKey: ['folder-prior-art', projectId, folderId],
    queryFn: () => getFolderPriorArt(projectId, folderId),
    enabled: !!projectId && !!folderId,
  })

  const reEvalMutation = useMutation({
    mutationFn: () => reEvaluateFolderPriorArt(projectId, folderId),
    onSuccess: (job) => setReprocessJob(job),
  })

  useEffect(() => {
    if (!reprocessJob || reprocessJob.status === 'completed' || reprocessJob.status === 'failed') return
    const interval = setInterval(async () => {
      try {
        const status = await getReprocessStatus(projectId, reprocessJob.job_id)
        setReprocessJob(status)
        if (status.status === 'completed') {
          queryClient.invalidateQueries({ queryKey: ['folder-prior-art', projectId, folderId] })
          clearInterval(interval)
        }
        if (status.status === 'failed') clearInterval(interval)
      } catch {
        clearInterval(interval)
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [reprocessJob, projectId, folderId, queryClient])

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    )
  }

  if (!priorArt) {
    return (
      <div className="text-center py-8">
        <Search className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">No prior art analysis yet. Generate AI Analysis first to enable prior art search.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {priorArt.overall_novelty_score != null
          ? <ScoreDisplay score={priorArt.overall_novelty_score} variant="novelty" size="lg" />
          : <span className="text-sm text-gray-500 italic">{priorArt.analysis_status === 'no_documents' ? 'No documents' : 'Analysis pending'}</span>
        }
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{priorArt.total_documents_analysed} documents analysed</span>
          <button
            onClick={() => reEvalMutation.mutate()}
            disabled={reEvalMutation.isPending || (reprocessJob?.status === 'in_progress')}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {(reEvalMutation.isPending || reprocessJob?.status === 'in_progress') ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Re-evaluating...</>
            ) : (
              <><RefreshCw className="w-3.5 h-3.5" /> Re-evaluate Prior Art</>
            )}
          </button>
        </div>
      </div>

      {priorArt.overlap_areas.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Overlap Areas</h4>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Area</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Severity</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Similarity</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {priorArt.overlap_areas.map((area, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900">{area.area}</td>
                    <td className="px-3 py-2">
                      {area.overlap_severity && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          area.overlap_severity === 'high'
                            ? 'bg-red-50 text-red-600'
                            : area.overlap_severity === 'medium'
                            ? 'bg-amber-50 text-amber-600'
                            : 'bg-green-50 text-green-600'
                        }`}>
                          {area.overlap_severity}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2"><ScoreDisplay score={area.similarity} variant="similarity" size="sm" /></td>
                    <td className="px-3 py-2 text-gray-600">{area.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {priorArt.related_papers.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Related Academic Papers</h4>
          <div className="space-y-2">
            {priorArt.related_papers
              .sort((a, b) => b.similarity - a.similarity)
              .map((paper) => (
                <div key={paper.paper_id} className="flex items-start gap-3 p-2.5 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{paper.title}</p>
                    <p className="text-xs text-gray-500">From {paper.source_documents.length} document{paper.source_documents.length !== 1 ? 's' : ''}</p>
                  </div>
                  <ScoreDisplay score={paper.similarity} variant="similarity" size="sm" />
                </div>
              ))}
          </div>
        </div>
      )}

      {priorArt.patent_matches.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Patent Matches</h4>
          <div className="space-y-2">
            {priorArt.patent_matches
              .sort((a, b) => b.similarity - a.similarity)
              .map((patent) => (
                <div key={patent.patent_id} className="flex items-start gap-3 p-2.5 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{patent.title}</p>
                    <p className="text-xs text-gray-500">{patent.patent_id}</p>
                  </div>
                  <ScoreDisplay score={patent.similarity} variant="similarity" size="sm" />
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
